import type { ObjectId } from 'mongodb';

export enum SundialEntityTypes {
  lightreach = 'sabalAccount',
}

export interface SundialAssociatedEntities {
  entityType: SundialEntityTypes;
  id: string;
}

export enum SundialPlatforms {
  tesla = 'tesla',
  solaredge = 'solaredge',
  enphase = 'enphase',
  generac = 'generac',
  generacV2 = 'generac-v2',
  tigo = 'tigo',
  ecobee = 'ecobee',
  nest = 'nest',
  schneider = 'schneider',
  franklin = 'franklin',
}

export enum SundialServiceTypes {
  production = 'production',
  consumption = 'consumption',
  gridConsumption = 'gridConsumption',
  battery = 'acb',
  encharge = 'encharge',
  thermostat = 'thermostat',
}

export interface SundialService {
  id: string;
  name: SundialPlatforms;
  username: string;
  password: string;
  types: SundialServiceTypes[];
}

export interface SystemInfo {
  timezone: string;
  zip: string;
  associatedEntities: SundialAssociatedEntities[];
  services: SundialService[];
}

export interface DataSource {
  sourceType: SundialServiceTypes;
  sourceService: SundialPlatforms;
  sourceId: string;
  serialNumber?: string;
}

export interface SundialSite {
  id: string;
  dataSources?: DataSource[];
  associatedEntities?: SundialAssociatedEntities[];
}

export interface SundialAssociatedEntities {
  entityType: SundialEntityTypes;
  id: string;
}

export interface SundialService {
  id: string;
  name: SundialPlatforms;
  username: string;
  password: string;
  types: SundialServiceTypes[];
}

export interface SystemInfo {
  timezone: string;
  zip: string;
  associatedEntities: SundialAssociatedEntities[];
  services: SundialService[];
}

export interface DataSource {
  sourceType: SundialServiceTypes;
  sourceService: SundialPlatforms;
  sourceId: string;
  serialNumber?: string;
}

export interface SundialSite {
  id: string;
  dataSources?: DataSource[];
  associatedEntities?: SundialAssociatedEntities[];
}

export interface AssetRequest {
  assetId: string;
}

interface PostalAddress {
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
}

interface Array {
  panelManufacturer: string;
  panelModel: string;
  inverterManufacturer: string;
  monitoringId: string;
  mountingType: string;
  mountingManufacturer: string;
  panelCount: number;
  tsrf: number;
  tilt: number;
  azimuth: number;
  orientation: string;
}

interface SolarAccessValues {
  january: number;
  february: number;
  march: number;
  april: number;
  may: number;
  june: number;
  july: number;
  august: number;
  september: number;
  october: number;
  november: number;
  december: number;
}

export enum OrganizationFunction {
  financier = 'Financier',
}

export interface Asset {
  _id: string;
  id: string;
  customerName: string;
  customerEmail: string;
  customerPhoneNumber: string;
  customerPostalAddress: PostalAddress;
  latitude: number;
  longitude: number;
  utilityName: string;
  operationalDate: Date;
  systemSize: number;
  yearOneProduction: number;
  monthlySystemProduction: number[];
  batteryManufacturer: string;
  batteryMonitoringId: string;
  organizationId: string;
  organizationType: OrganizationFunction;
  contractType: string;
  arrays: Array[];
  solarAccessValues: SolarAccessValues;
}

export interface SitesQuery {
  associatedEntity?: SundialAssociatedEntities;
  page?: number;
  pageSize?: number;
}

export interface PaginatedListResponse<T> {
  data: T[];
  totalCount: number;
}

export interface UpsertSiteDataSourceRequest {
  sourceId: string;
  sourceService: SundialPlatforms;
}

export enum EnergyType {
  production = 'production',
  consumption = 'consumption',
  storage = 'storage',
  gridConsumption = 'gridConsumption',
}

export enum SundialBatteryType {
  acb = 'acb',
  encharge = 'encharge',
}

export enum DeviceType {
  thermostat = 'thermostat',
}

export enum GridType {
  netGridConsumption = 'netGridConsumption',
  gridExport = 'gridExport',
}

export enum HardwareType {
  inverter = 'inverter',
  battery = 'battery',
}

export type ReadingsType = EnergyType | SundialBatteryType | DeviceType | GridType;

export type SundialHardwareLookup = {
  serialNumber?: string;
  manufacturer?: string;
  model?: string;
  sku?: string | null;
};

export interface Hardware {
  manufacturer: string;
  model: string;
  serialNumber: string;
  sku?: string;
  partNumber?: string;
  connectedTo?: string;
  status?: string;
  name?: string;
  enabled?: boolean;
}

export interface Meter extends Hardware {
  meterType?: ReadingsType; // | SolarEdgeMeterType;
  firmwareVersion?: string;
}

export interface Inverter extends Omit<Hardware, 'connectedTo'> {
  connectedOptimizers?: number;
  isMicroInverter: boolean;
  cpuVersion?: string;
  communicationType?: string;
}

export interface Gateway extends Omit<Hardware, 'connectedTo'> {
  firmwareVersion?: string;
  communicationType?: string;
  enabled?: boolean;
}

export interface Sensor extends Omit<Hardware, 'model' | 'serialNumber'> {
  category: string;
  type: string;
  id: string;
}

export interface Modem {
  imei: string;
  sku: string;
  partNumber: string;
  communicationType: string;
}

export interface Battery extends Omit<Hardware, 'connectedTo'> {
  batteryType: SundialBatteryType;
  capacity?: string;
  firmwareVersion?: string;
}

export interface Relay extends Hardware {}

export interface Controller extends Hardware {}

export interface Optimizers extends Hardware {}

export type SiteHardwareCategories = Omit<SiteHardware, 'siteId' | 'createdAt' | 'updatedAt'>;

export interface HVACItem {
  model: string;
  manufacturer: string;
  type: string;
  serialNumber: string;
  name: string;
}
export interface SiteHardware {
  siteId: ObjectId;
  createdAt?: Date;
  updatedAt?: Date;
  gateways: Gateway[];
  inverters: Inverter[];
  meters: Meter[];
  optimizers: Optimizers[];
  controllers: Controller[];
  relays: Relay[];
  sensors: Sensor[];
  batteries: Battery[];
  modems: Modem[];
  otherHardware: Hardware[];
  hvacEquipment?: HVACItem[];
  hvacAccessories?: HVACItem[];
}

export interface PrefetchedSundialData {
  site: SundialSite;
  hardware?: SiteHardware;
}

/**
 * Monitoring ID format rules per manufacturer, keyed by lowercased SundialPlatforms value.
 * Shared by client and server so the two validation paths can't drift.
 * A manufacturer with no entry here has no enforced format.
 */
const NUMERIC_ID_REGEX = /^\d{5,9}$/;
const NUMERIC_ID_EXPECTED_FORMAT = '5 to 9 digit numeric ID';

export const MONITORING_ID_FORMATS: Record<string, { regex: RegExp; expectedFormat: string }> = {
  [SundialPlatforms.tesla]: {
    regex: /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
    expectedFormat:
      'UUID v4 format, taken from the URL ' +
      '(e.g., 8af307d1-ca9f-4843-8079-df2ba9c5736c from https://powerhub.energy.tesla.com/site/8af307d1-ca9f-4843-8079-df2ba9c5736c)',
  },
  [SundialPlatforms.franklin]: {
    regex: /^[a-zA-Z0-9]{20}$/,
    expectedFormat: '20-character alphanumeric string',
  },
  [SundialPlatforms.generac]: {
    regex: /^([0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|\d{5,9})$/i,
    expectedFormat: 'UUID v4 format (e.g., 550e8400-e29b-41d4-a716-446655440000) or 5 to 9 digit numeric ID',
  },
  [SundialPlatforms.generacV2]: {
    regex: /^([0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|\d{5,9})$/i,
    expectedFormat: 'UUID v4 format (e.g., 550e8400-e29b-41d4-a716-446655440000) or 5 to 9 digit numeric ID',
  },
  [SundialPlatforms.enphase]: {
    regex: NUMERIC_ID_REGEX,
    expectedFormat: NUMERIC_ID_EXPECTED_FORMAT,
  },
  [SundialPlatforms.solaredge]: {
    regex: NUMERIC_ID_REGEX,
    expectedFormat: NUMERIC_ID_EXPECTED_FORMAT,
  },
  qcells: {
    regex: NUMERIC_ID_REGEX,
    expectedFormat: NUMERIC_ID_EXPECTED_FORMAT,
  },
};

export interface MonitoringIdFormatValidation {
  valid: boolean;
  expectedFormat: string;
}

export const validateMonitoringIdFormat = (
  monitoringId?: string,
  manufacturer?: string,
): MonitoringIdFormatValidation => {
  const rule = MONITORING_ID_FORMATS[manufacturer?.trim().toLowerCase() ?? ''];

  if (!rule) {
    return { valid: true, expectedFormat: 'No specific format required' };
  }

  return {
    valid: rule.regex.test((monitoringId ?? '').trim()),
    expectedFormat: rule.expectedFormat,
  };
};
